<template>
  <div id="settings-index" class="settings-container">
    <!-- Header com botão de voltar -->
    <div class="header-top">
      <v-btn
        icon
        small
        @click="$router.back()"
        class="back-button"
      >
        <v-icon color="#1D1D1F">mdi-arrow-left</v-icon>
      </v-btn>
    </div>

    <!-- Título alinhado à esquerda -->
    <div class="header-title">
      <div>
        <h1 class="text-h5 font-weight-bold">Configurações do Sistema</h1>
        <p class="text-caption grey--text mb-0">Gerencie as configurações da plataforma</p>
      </div>
    </div>

    <!-- Cards Grid - Centralizado -->
    <div class="cards-grid">
      <!-- Card: Conta SaaS -->
      <v-card
        v-if="canAccessAdminSettings"
        class="settings-card"
        outlined
        @click="$router.push('/settings/saas-account')"
      >
        <v-card-title class="d-flex align-center pb-3">
          <v-icon large color="purple" class="mr-3">mdi-server-network</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold">Conta SaaS</div>
          </div>
        </v-card-title>
        <v-card-text class="pb-4">
          <p class="text-body-2 mb-2">Gerenciar a conta SaaS principal do sistema.</p>
          <p class="text-caption grey--text mb-0">Crie ou visualize a conta mãe do sistema.</p>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn text color="primary" small>Acessar →</v-btn>
        </v-card-actions>
      </v-card>

      <!-- Card: Tenant -->
      <v-card
        v-if="canAccessAdminSettings"
        class="settings-card"
        outlined
        @click="$router.push('/settings/tenant')"
      >
        <v-card-title class="d-flex align-center pb-3">
          <v-icon large color="blue" class="mr-3">mdi-domain</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold">Tenant</div>
          </div>
        </v-card-title>
        <v-card-text class="pb-4">
          <p class="text-body-2 mb-2">Configurar o tenant administrativo do sistema.</p>
          <p class="text-caption grey--text mb-0">Criar ou editar informações do tenant.</p>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn text color="primary" small>Acessar →</v-btn>
        </v-card-actions>
      </v-card>

      <!-- Card: Platform Owner -->
      <v-card
        v-if="isPlatformOwner"
        class="settings-card"
        outlined
        @click="$router.push('/settings/platform-owner')"
      >
        <v-card-title class="d-flex align-center pb-3">
          <v-icon large color="orange" class="mr-3">mdi-shield-crown</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold">Platform Owner</div>
          </div>
        </v-card-title>
        <v-card-text class="pb-4">
          <p class="text-body-2 mb-2">Criar o usuário super administrador da plataforma.</p>
          <p class="text-caption grey--text mb-0">Usuário com acesso total ao sistema.</p>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn text color="primary" small>Acessar →</v-btn>
        </v-card-actions>
      </v-card>

      <!-- Card: Partner Admin -->
      <v-card
        v-if="isPlatformOwner"
        class="settings-card"
        outlined
        @click="$router.push('/settings/partner-admin')"
      >
        <v-card-title class="d-flex align-center pb-3">
          <v-icon large color="green" class="mr-3">mdi-account-tie</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold">Partner Admin</div>
          </div>
        </v-card-title>
        <v-card-text class="pb-4">
          <p class="text-body-2 mb-2">Criar administrador para parceiros específicos.</p>
          <p class="text-caption grey--text mb-0">Gerenciar usuários administradores de parceiros.</p>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn text color="primary" small>Acessar →</v-btn>
        </v-card-actions>
      </v-card>

      <!-- Card: Configurações de Gestor -->
      <v-card
        v-if="canAccessManagerSettings"
        class="settings-card"
        outlined
        @click="$router.push('/settings/manager')"
      >
        <v-card-title class="d-flex align-center pb-3">
          <v-icon large color="indigo" class="mr-3">mdi-account-cog</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold">Configurações de Gestor</div>
          </div>
        </v-card-title>
        <v-card-text class="pb-4">
          <p class="text-body-2 mb-2">Configurar preferências de gestão de equipe.</p>
          <p class="text-caption grey--text mb-0">Notificações, metas, relatórios e equipe.</p>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn text color="primary" small>Acessar →</v-btn>
        </v-card-actions>
      </v-card>

      <!-- Card: Minhas Configurações -->
      <v-card
        class="settings-card"
        outlined
        @click="$router.push('/settings/user')"
      >
        <v-card-title class="d-flex align-center pb-3">
          <v-icon large color="teal" class="mr-3">mdi-account-circle</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold">Minhas Configurações</div>
          </div>
        </v-card-title>
        <v-card-text class="pb-4">
          <p class="text-body-2 mb-2">Configurações pessoais de perfil e preferências.</p>
          <p class="text-caption grey--text mb-0">Perfil, notificações, privacidade e aparência.</p>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn text color="primary" small>Acessar →</v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Info Alert -->
    <div class="info-section">
      <v-alert
        text
        color="info"
        icon="mdi-information"
        border="left"
        class="alert-box"
      >
        <div class="text-subtitle-2 mb-1">
          📋 Informações Importantes
        </div>
        <ul class="text-caption">
          <li v-if="canAccessAdminSettings">Execute as configurações na ordem: <strong>SaaS Account → Tenant → Platform Owner</strong></li>
          <li v-if="canAccessAdminSettings">A conta SaaS deve ser criada primeiro para habilitar as demais funcionalidades</li>
          <li v-if="isPlatformOwner">Partner Admin é opcional e deve ser criado apenas quando necessário</li>
          <li v-if="canAccessManagerSettings">Como gestor, você pode configurar preferências para sua equipe</li>
          <li>Todas as configurações são salvas automaticamente</li>
        </ul>
      </v-alert>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SettingsIndex',

  computed: {
    currentUser () {
      try {
        const userStr = localStorage.getItem('user')
        return userStr ? JSON.parse(userStr) : null
      } catch (error) {
        console.error('Erro ao recuperar usuário:', error)
        return null
      }
    },

    isPlatformOwner () {
      return this.currentUser?.role === 'PLATFORM_OWNER'
    },

    canAccessAdminSettings () {
      return ['PLATFORM_OWNER', 'TENANT_ADMIN'].includes(this.currentUser?.role)
    },

    canAccessManagerSettings () {
      return ['MANAGER', 'TENANT_ADMIN', 'PLATFORM_OWNER'].includes(this.currentUser?.role)
    },
  },

  data: () => ({
    //
  }),
}
</script>

<style scoped>
/* Container principal */
#settings-index {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #F5F5F7 0%, #f0f2f5 100%);
}

.theme--dark #settings-index {
  background: linear-gradient(135deg, #121212 0%, #1C1C1E 100%);
}

/* Header top com botão de voltar */
.header-top {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 100;
}

.back-button {
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.theme--dark .back-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Header title alinhado à esquerda no topo */
.header-title {
  width: 100%;
  max-width: 900px;
  margin-top: 0;
  margin-bottom: 2rem;
  text-align: left;
}

.header-title h1 {
  letter-spacing: -0.02em;
  margin-bottom: 0.25rem;
}

/* Cards Grid - Centralizado e compacto */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  max-width: 900px;
  width: 100%;
  margin-bottom: 3rem;
}

/* Card styling - Compacto */
.settings-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px !important;
  background: white !important;
  border: 1px solid #E5E5E7 !important;
}

.theme--dark .settings-card {
  background: #1C1C1E !important;
  border-color: #38383A !important;
}

.settings-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
}

.theme--dark .settings-card:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 119, 28, 0.1) !important;
}

/* Info section */
.info-section {
  max-width: 900px;
  width: 100%;
}

.alert-box {
  border-radius: 12px !important;
  background: white !important;
}

.theme--dark .alert-box {
  background: #1C1C1E !important;
}

/* Responsive */
@media (max-width: 768px) {
  #settings-index {
    padding: 1rem;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .header-title {
    margin-bottom: 1.5rem;
  }
}
</style>
